import SbTextField from '@Components/Textfield';
import { Box, Card, CardContent, Chip, Typography, alpha, styled } from '@mui/material';
import React, { useEffect, useState } from 'react';
import SendIcon from '@mui/icons-material/Send';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { addAdminShipmentNotes, getAdminShipmentNotes } from '@Reducers/AdminShipment';
import { ShipmentNotesType } from '@Reducers/AdminShipment/Type';

import { useParams } from 'react-router-dom';
import moment from 'moment';
import SearchDropdown from '@Components/SearchDropdown';
import { getAdminListForTag } from '@Reducers/Admin/actions';
import { OptionType } from '@Components/SearchDropdown/Type';
import { isActionAllowed } from '../../../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { ShipmentControl } from '@Services/shipmentActionControl';

function ShipmentNote() {
  const { sn: shipmentNumber } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const AdminList = useSelector((state: RootStateType) => state.admin.adminListForTag).map(admin => ({
    display: admin.username,
    value: admin._id,
  }));
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const shipmentNotes = useSelector((state: RootStateType) => state.adminShipment.shipmentNotes);

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isShipmentNotesWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_notes,
    PERFORM_ACTION.write,
  );

  const [taggedUsers, setTaggedUsers] = useState<OptionType[]>([]);

  useEffect(() => {
    dispatch(getAdminListForTag());
  }, []);
  useEffect(() => {
    if (shipmentNumber) dispatch(getAdminShipmentNotes(shipmentNumber));
  }, [shipmentNumber]);

  return (
    <Card
      sx={{
        borderRadius: '14px',
        border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        bgcolor: 'background.paper',
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            }}
          >
            <RateReviewOutlinedIcon sx={{ fontSize: '20px' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: 'primary.main',
                  letterSpacing: '-0.01em',
                }}
              >
                Shipment Notes & Instructions
              </Typography>
              <Chip
                label="Notes"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '12px',
                  fontWeight: 500,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  borderRadius: '6px',
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary', mt: 0.25 }}>
              Internal remarks, special handling instructions, and user notes
            </Typography>
          </Box>
        </Box>
      </Box>

      <CardContent
        sx={{
          p: 3,
          '&:last-child': { pb: 3 },
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRadius: '10px',
            bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
            border: theme => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            mb: 3,
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>User Notes:</Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary', wordBreak: 'break-word' }}>
            {shipmentById?.shipmentNotes || '-'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.75 }}>
                Admin Notes
              </Typography>
              <SearchDropdown
                label=""
                multiple
                id="company-country-id"
                placeholder="Tag Admin"
                options={AdminList}
                value={taggedUsers}
                hideStartAdornment
                onChange={value => {
                  if (value) setTaggedUsers(value);
                }}
                disabled={!isShipmentNotesWriteAllowed}
              />
            </Box>
            <AddNote
              taggedUsers={taggedUsers}
              resetUsers={() => setTaggedUsers([])}
              disabled={!isShipmentNotesWriteAllowed}
            />
          </Box>
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 1.5 }}>
              Notes History
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                maxHeight: 280,
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
              {shipmentNotes && shipmentNotes.length > 0 ? (
                shipmentNotes.map(note => <SingleNote key={note.createdAt} data={note} />)
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>No notes added yet</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const SendNoteBtn = styled(SendIcon)(() => ({
  position: 'absolute',
  bottom: '16px',
  right: '16px',
}));

function AddNote({
  taggedUsers,
  resetUsers,
  disabled,
}: {
  taggedUsers: OptionType[];
  resetUsers: () => void;
  disabled?: boolean;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const { sn: shipmentNumber } = useParams();
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const shipmentActionControl = new ShipmentControl(shipmentById);
  const [note, setNote] = useState<string>('');
  const onAddNote = async () => {
    if (note.trim() && shipmentNumber) {
      await dispatch(
        addAdminShipmentNotes({
          id: shipmentNumber,
          message: note.trim(),
          _taggedUserId: taggedUsers.map(user => user.value),
        }),
      );
      setNote('');
      resetUsers();
    }
  };
  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <SbTextField
        className="w-full"
        value={note}
        onChange={e => setNote(e.target.value)}
        multiline
        rows={4}
        endAdornment={
          shipmentActionControl.canUpdateNotes ? <SendNoteBtn className="cursor-pointer" onClick={onAddNote} /> : <></>
        }
        disabled={disabled}
      />
    </Box>
  );
}

const SingleNoteContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: '10px',
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
}));

interface SingleNoteProps {
  data: ShipmentNotesType;
}

function SingleNote(props: SingleNoteProps) {
  const { message, _userId, createdAt, _taggedUserId } = props.data;
  const date = moment(createdAt).format('DD MMM YYYY');
  const time = moment(createdAt).format('hh:mm');
  return (
    <SingleNoteContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5,
          flexWrap: 'wrap',
          gap: 0.5,
        }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'common.black' }}>
          {_userId.name}{' '}
          <Typography component="span" sx={{ fontSize: '12px', fontWeight: 400, color: 'text.secondary', ml: 0.5 }}>
            {time}
          </Typography>
        </Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 400, color: 'text.secondary' }}>{date}</Typography>
      </Box>
      <Typography
        sx={{ fontSize: '13px', color: 'text.primary', whiteSpace: 'pre-line', my: 0.75, wordBreak: 'break-word' }}
      >
        {message}
        {_taggedUserId?.map(user => (
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }} key={user._id || user.username}>
            {` @${user.username}`}
          </Box>
        ))}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: 'text.secondary', wordBreak: 'break-all' }}>
        {_userId.email}
      </Typography>
    </SingleNoteContainer>
  );
}

export default ShipmentNote;
